import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { WalletKeySelectorComponent } from '../../shared/design-system/wallet-key-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { PAYMENTS_LIST_PAGE_STYLES, paymentStatusLabelKey } from './payments-list-page.utils';
import {
  PaymentsAdminService,
  WalletAccountRow,
  WalletHoldRow,
  WalletLedgerRow,
  WalletPayoutRow,
  WalletReconciliationSummaryRow,
  WalletTopUpRow
} from './payments-admin.service';

type WalletTab = 'accounts' | 'ledger' | 'holds' | 'topups' | 'payouts';

@Component({
  standalone: true,
  selector: 'ain-payments-wallet',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    CheckboxModule,
    TagModule,
    AinCanAccessDirective,
    CurrencySelectorComponent,
    WalletKeySelectorComponent,
    AinAmountPipe
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.wallet.title')" [description]="t('payments.wallet.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.wallet.help.page.title'"
        [descriptionKey]="'payments.wallet.help.page.description'"
        [bulletKeys]="[
          'payments.wallet.help.page.bullets.create',
          'payments.wallet.help.page.bullets.tabs',
          'payments.wallet.help.page.bullets.summary'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.wallet.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.wallet.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          @if (summary()) {
            <div class="section-title">
              <span>{{ t('payments.wallet.help.summary.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.wallet.help.summary.title'"
                [descriptionKey]="'payments.wallet.help.summary.description'" />
            </div>
            <section class="summary-grid">
              <div><span>{{ t('payments.wallet.summary.active') }}</span><strong>{{ summary()!.activeAccounts | ainAmount }}</strong></div>
              <div><span>{{ t('payments.wallet.summary.available') }}</span><strong>{{ summary()!.totalAvailableBalance | ainAmount }}</strong></div>
              <div><span>{{ t('payments.wallet.summary.holds') }}</span><strong>{{ summary()!.activeHolds | ainAmount }}</strong></div>
              <div><span>{{ t('payments.wallet.summary.pendingPayouts') }}</span><strong>{{ summary()!.pendingPayouts | ainAmount }}</strong></div>
            </section>
          }

          <div class="toolbar">
            <button
              pButton
              type="button"
              [label]="t('common.create')"
              ainCanAccess
              [ainCanAccessPermission]="'payments.wallet'"
              (click)="openCreateDialog()">
            </button>
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.wallet.help.filters.title'"
                [descriptionKey]="'payments.wallet.help.filters.description'"
                [bulletKeys]="['payments.wallet.help.filters.bullets.search']" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('columns.currency') }}</span>
                <ain-currency-selector [(ngModel)]="currencyFilter" name="currencyFilter" />
              </label>
            </div>
          </details>

          <nav class="tabs">
            @for (tab of tabItems; track tab.id) {
              <button type="button" [class.active]="activeTab() === tab.id" (click)="activeTab.set(tab.id)">{{ t(tab.labelKey) }}</button>
            }
          </nav>

          @switch (activeTab()) {
            @case ('accounts') {
              <div class="section-title">
                <span>{{ t('payments.wallet.tabs.accounts') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.wallet.help.tabs.accounts.title'"
                  [descriptionKey]="'payments.wallet.help.tabs.accounts.description'" />
              </div>
              @if (filteredAccounts().length === 0) {
                <ain-state type="empty" [title]="t('payments.wallet.states.empty')" [message]="t('payments.wallet.states.emptyMessage')" />
              } @else {
                <p-table [value]="filteredAccounts()" dataKey="id" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('payments.wallet.walletKey') }}</th>
                      <th>{{ t('payments.wallet.ownerType') }}</th>
                      <th>{{ t('columns.currency') }}</th>
                      <th>{{ t('payments.wallet.balance') }}</th>
                      <th>{{ t('payments.wallet.available') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.walletKey }}</td>
                      <td>{{ ownerTypeLabel(row.ownerType) }}</td>
                      <td>{{ row.currencyCode }}</td>
                      <td>{{ (row.balance ?? row.ledgerBalance) | ainAmount }}</td>
                      <td>{{ row.availableBalance | ainAmount }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            }
            @case ('ledger') {
              <div class="section-title">
                <span>{{ t('payments.wallet.tabs.ledger') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.wallet.help.tabs.ledger.title'"
                  [descriptionKey]="'payments.wallet.help.tabs.ledger.description'" />
              </div>
              <p-table [value]="filteredLedger()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.wallet.entryType') }}</th>
                    <th>{{ t('payments.wallet.direction') }}</th>
                    <th>{{ t('payments.wallet.amount') }}</th>
                    <th>{{ t('payments.wallet.balanceAfter') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.entryType }}</td>
                    <td>{{ row.direction }}</td>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td>{{ row.balanceAfter | ainAmount }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @case ('holds') {
              <div class="section-title">
                <span>{{ t('payments.wallet.tabs.holds') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.wallet.help.tabs.holds.title'"
                  [descriptionKey]="'payments.wallet.help.tabs.holds.description'" />
              </div>
              <p-table [value]="filteredHolds()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.wallet.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.wallet.walletKey') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td>{{ row.walletAccountId }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @case ('topups') {
              <div class="section-title">
                <span>{{ t('payments.wallet.tabs.topups') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.wallet.help.tabs.topups.title'"
                  [descriptionKey]="'payments.wallet.help.tabs.topups.description'" />
              </div>
              <p-table [value]="filteredTopUps()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.wallet.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @case ('payouts') {
              <div class="section-title">
                <span>{{ t('payments.wallet.tabs.payouts') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.wallet.help.tabs.payouts.title'"
                  [descriptionKey]="'payments.wallet.help.tabs.payouts.description'" />
              </div>
              <div class="toolbar">
                <button
                  pButton
                  type="button"
                  [label]="t('payments.wallet.batchApprovePayouts')"
                  [disabled]="selectedPayoutIds().length === 0 || batchApproving()"
                  (click)="batchApproveSelectedPayouts()"></button>
              </div>
              <p-table
                [value]="filteredPayouts()"
                [paginator]="true"
                [rows]="10"
                dataKey="id"
                [(selection)]="selectedPayoutRows">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"><p-tableHeaderCheckbox /></th>
                    <th>{{ t('payments.wallet.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.wallet.sellerId') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><p-tableCheckbox [value]="row" /></td>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td>{{ row.sellerId ?? t('payments.common.notAvailable') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }

          @if (actionError()) { <p class="error">{{ actionError() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('payments.wallet.createTitle')"
        [modal]="true"
        [(visible)]="createDialogVisible"
        [style]="{ width: '520px' }">
        <div class="dialog-grid">
          <label>
            <span class="label-row">
              {{ t('payments.wallet.ownerType') }}
              <ain-info-hint
                [titleKey]="'payments.wallet.help.create.title'"
                [descriptionKey]="'payments.wallet.help.create.description'" />
            </span>
            <p-select
              [options]="ownerTypeOptions"
              optionValue="value"
              [(ngModel)]="createModel.ownerType"
              name="createOwnerType"
              appendTo="body"
              (ngModelChange)="onOwnerTypeChanged()">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('payments.wallet.walletKey') }}</span>
            <ain-wallet-key-selector
              [(ngModel)]="createModel.walletKey"
              name="createWalletKey"
              [ownerType]="createModel.ownerType" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="createModel.currencyCode" name="createCurrencyCode" />
          </label>
          <label class="checkbox wide">
            <p-checkbox binary="true" [(ngModel)]="createModel.allowNegativeBalance" name="createAllowNegative" />
            <span>{{ t('payments.wallet.allowNegative') }}</span>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creating()" (click)="createWallet()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .checkbox { display: flex; gap: 8px; align-items: center; }
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsWalletComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly localizationService = inject(LocalizationService);

  readonly tabItems: { id: WalletTab; labelKey: string }[] = [
    { id: 'accounts', labelKey: 'payments.wallet.tabs.accounts' },
    { id: 'ledger', labelKey: 'payments.wallet.tabs.ledger' },
    { id: 'holds', labelKey: 'payments.wallet.tabs.holds' },
    { id: 'topups', labelKey: 'payments.wallet.tabs.topups' },
    { id: 'payouts', labelKey: 'payments.wallet.tabs.payouts' }
  ];
  readonly activeTab = signal<WalletTab>('accounts');

  readonly rows = signal<WalletAccountRow[]>([]);
  readonly ledgerRows = signal<WalletLedgerRow[]>([]);
  readonly holdRows = signal<WalletHoldRow[]>([]);
  readonly topUpRows = signal<WalletTopUpRow[]>([]);
  readonly payoutRows = signal<WalletPayoutRow[]>([]);
  selectedPayoutRows: WalletPayoutRow[] = [];
  readonly batchApproving = signal(false);
  readonly summary = signal<WalletReconciliationSummaryRow | null>(null);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly search = signal('');
  currencyFilter: string | null = null;
  createDialogVisible = false;
  createModel = { walletKey: '', currencyCode: 'IRR', ownerType: 'Buyer', allowNegativeBalance: false };

  readonly ownerTypeOptions = [
    { value: 'Buyer', labelKey: 'payments.wallet.ownerTypes.Buyer' },
    { value: 'Seller', labelKey: 'payments.wallet.ownerTypes.Seller' },
    { value: 'Platform', labelKey: 'payments.wallet.ownerTypes.Platform' },
    { value: 'Escrow', labelKey: 'payments.wallet.ownerTypes.Escrow' }
  ];

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);
    this.api.listWalletAccounts().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('payments.wallet.errorLoad'));
        this.loading.set(false);
      }
    });
    this.api.listLedger().subscribe({ next: rows => this.ledgerRows.set(rows) });
    this.api.listHolds().subscribe({ next: rows => this.holdRows.set(rows) });
    this.api.listTopUpIntents().subscribe({ next: rows => this.topUpRows.set(rows) });
    this.api.listPayoutRequests().subscribe({ next: rows => this.payoutRows.set(rows) });
    this.api.getReconciliationSummary().subscribe({ next: summary => this.summary.set(summary) });
  }

  openCreateDialog(): void {
    this.createModel = { walletKey: '', currencyCode: 'IRR', ownerType: 'Buyer', allowNegativeBalance: false };
    this.actionError.set(null);
    this.createDialogVisible = true;
  }

  onOwnerTypeChanged(): void {
    this.createModel.walletKey = '';
  }

  filteredAccounts(): WalletAccountRow[] {
    return this.applyFilters(this.rows(), row => [row.walletKey, row.ownerType, row.currencyCode]);
  }

  filteredLedger(): WalletLedgerRow[] {
    return this.applyFilters(this.ledgerRows(), row => [row.entryType, row.direction, row.currencyCode]);
  }

  filteredHolds(): WalletHoldRow[] {
    return this.applyFilters(this.holdRows(), row => [row.status, row.currencyCode, row.walletAccountId]);
  }

  filteredTopUps(): WalletTopUpRow[] {
    return this.applyFilters(this.topUpRows(), row => [row.status, row.currencyCode]);
  }

  filteredPayouts(): WalletPayoutRow[] {
    return this.applyFilters(this.payoutRows(), row => [row.status, row.currencyCode, row.sellerId]);
  }

  selectedPayoutIds(): string[] {
    return this.selectedPayoutRows.map(row => row.id);
  }

  batchApproveSelectedPayouts(): void {
    const ids = this.selectedPayoutIds();
    if (ids.length === 0) return;
    this.batchApproving.set(true);
    this.actionError.set(null);
    this.api.batchApprovePayoutRequests(ids).subscribe({
      next: () => {
        this.selectedPayoutRows = [];
        this.batchApproving.set(false);
        this.api.listPayoutRequests().subscribe({ next: rows => this.payoutRows.set(rows) });
      },
      error: () => {
        this.batchApproving.set(false);
        this.actionError.set(this.t('payments.wallet.batchApproveFailed'));
      }
    });
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  createWallet(): void {
    const payload = {
      walletKey: this.createModel.walletKey.trim(),
      currencyCode: this.createModel.currencyCode.trim(),
      ownerType: this.createModel.ownerType,
      allowNegativeBalance: this.createModel.allowNegativeBalance
    };
    if (!payload.walletKey || !payload.currencyCode) {
      return;
    }

    this.creating.set(true);
    this.api.createFoundationWalletAccount(payload).subscribe({
      next: created => {
        this.rows.update(rows => [
          {
            id: created.id,
            walletKey: created.walletKey,
            currencyCode: created.currencyCode,
            balance: created.ledgerBalance,
            reservedBalance: created.heldBalance,
            availableBalance: created.availableBalance,
            allowNegativeBalance: created.allowNegativeBalance,
            ownerType: created.ownerType
          },
          ...rows
        ]);
        this.createModel.walletKey = '';
        this.creating.set(false);
        this.createDialogVisible = false;
      },
      error: () => {
        this.creating.set(false);
        this.actionError.set(this.t('payments.wallet.errorCreate'));
      }
    });
  }

  ownerTypeLabel(ownerType: string | null | undefined): string {
    if (!ownerType) {
      return this.t('payments.wallet.ownerTypes.Buyer');
    }

    const key = `payments.wallet.ownerTypes.${ownerType}`;
    const translated = this.t(key);
    return translated === key ? ownerType : translated;
  }

  statusLabel(status: string): string {
    const key = paymentStatusLabelKey(status);
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    const normalized = status.toLowerCase();
    if (normalized.includes('fail') || normalized.includes('cancel')) {
      return 'danger';
    }
    if (normalized.includes('pending') || normalized.includes('open')) {
      return 'warn';
    }
    if (normalized.includes('complete') || normalized.includes('active')) {
      return 'success';
    }
    return 'info';
  }

  private applyFilters<T>(rows: T[], fields: (row: T) => Array<string | number | null | undefined>): T[] {
    const value = this.search().trim().toLowerCase();
    return rows.filter(row => {
      if (this.currencyFilter) {
        const currency = (row as { currencyCode?: string }).currencyCode;
        if (currency && currency !== this.currencyFilter) {
          return false;
        }
      }

      if (!value) {
        return true;
      }

      return fields(row).some(field => String(field ?? '').toLowerCase().includes(value));
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
